/* eslint-disable max-len */
/* eslint-disable import/no-cycle */
/**
 @format
 @flow
 */

import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import Colors from '@ziynx/theme/colors';
import Link from '@ziynx/components/links';
import { Constants } from '@ziynx/components/layout';
import { Heading } from '@ziynx/components/typography';
import Icon from '@ziynx/components/icons';
import type { TextLinkType } from '@ziynx/flow-types/links';
import VideoPlayer from './VideoPlayer';
import ImageOverlay from './ImageOverlay';

 type Props = $ReadOnly<{|
   /**
    * Optional width,
    */
   width?: string | number,
   /**
    * Size variations of the VideoSquare
    */
   size?: 'preview' | 'small' | 'large',
   /**
    * Video source
    */
   videoSource?: string,
   /**
    * Image overlay source
    */
   imageSource?: string,
   small?: boolean,
   mini?: boolean,
   boxShadow?: boolean,
   textLink?: TextLinkType,
   onEnd?: () => void,
   playButtonPosition?: 'bottomRight' | 'center',
 |}>;

const getMinDimensions = (size) => {
  switch (size) {
  case 'preview':
    return '50px';
  case 'large':
    return '473px';
  case 'small':
    return '192px';
  default:
    return '192px';
  }
};

const getMaxDimensions = (size) => {
  switch (size) {
  case 'preview':
    return '50px';
  case 'large':
    return '100%';
  case 'small':
    return '472px';
  default:
    return '100%';
  }
};

const getBorderRadius = (size) => {
  switch (size) {
  case 'preview':
    return '8px';
  default:
    return '24px';
  }
};

const getIconPadding = (size) => {
  switch (size) {
  case 'preview':
    return '4px';
  case 'large':
    return '24px';
  case 'small':
  default:
    return '16px';
  }
};

const Wrapper = styled.div`
  width: ${({ width }) => (width || '100%')};
  min-width: ${({ minSize }) => (minSize || '192px')};
  max-width: ${({ maxSize }) => (maxSize || '688px')};
  position: relative;
`;

const VideoWrapper = styled.div`
   display: flex;
   justify-content: center;
   align-items: center;
   z-index: 0;
   border-radius: ${({ size }) => getBorderRadius(size)};
   box-shadow: ${({ boxShadow }) => boxShadow && Constants.boxShadowTotal};
   overflow: hidden;
   cursor: pointer;
   width: 100%;
   aspect-ratio: 1 / 1;
   -webkit-backface-visibility: hidden;
   -webkit-transform: translate3d(0, 0, 0);
   @supports not (aspect-ratio: auto) {
      padding-top: 100%;
      height: 0;
      overflow: hidden;
      -webkit-transform: translateZ(0);
   }
 `;

const ImageWrapper = styled.div`
   position: absolute;
   top: 0;
   left: 0;
   width: 100%;
   display: flex;
   justify-content: center;
   border-radius: ${({ size }) => getBorderRadius(size)};
   box-shadow: ${({ boxShadow }) => boxShadow && Constants.boxShadowTotal};
   cursor: pointer;
   aspect-ratio: 1 / 1;
   overflow: hidden;
   opacity: 1;
   transition: opacity 1000ms ease-in-out, height 1000ms;
   ${({ showOverlay }) => !showOverlay && 'opacity: 0; height: 0; transition: opacity 1000ms ease-in-out, height 1000ms;'}
   @supports not (aspect-ratio: 1 / 1) {
    padding-top: 100%;
    height: 0;
    overflow: hidden;
    ${({ showOverlay }) => !showOverlay && 'padding-top: 0; transition: padding-top 1000ms;'}
   };
   ${({ noVideoSource }) => noVideoSource && 'background-color: white;'};
 `;

const StyledVideoPlayer = styled(VideoPlayer)`
    max-height: 100%;
    @supports not (aspect-ratio: auto) { 
     position: absolute;
     transform: translate(-50%, -50%);
     left: 50%;
     top: 50%;
     width: 100%;
     max-width: 100%; 
     height: auto; 
   };
   :-webkit-full-screen {
     transform: none;
   }
 `;

const NoVideoWrapper = styled.div`
   display: flex;
   flex-direction: column;
   text-align: center;
   justify-content: center;
   align-items: center;
   border: 2px dashed ${Colors.GrayLight};
   border-radius: ${({ size }) => getBorderRadius(size)}; 
   box-sizing: border-box;
   width: ${({ width }) => (width || '100%')};
   aspect-ratio: 1 / 1;
   @supports not (aspect-ratio: 1 / 1) {
    padding-top: 100%;
    position: relative;
    height: 0;
    overflow: hidden;
   };
 `;

const EmptyStateWrapper = styled.div`
    max-width: 100%;
    @supports not (aspect-ratio: 1 / 1) {
      position: absolute;
      transform: translate(-50%, -50%);
      left: 50%;
      top: 50%;
      width: auto;
      max-width: 100%; 
      height: auto;
    };
 `;

const IconWrapper = styled.div`
  max-width: 100%;
  @supports not (aspect-ratio: 1 / 1) {
        position: absolute;
        transform: translate(-50%, -50%);
        left: 50%;
        top: 50%;
        width: auto;
        max-width: 100%; 
        height: auto;
      };
 `;

const VideoSquare = (props: Props): React$Node => {
  const {
    size, small, mini, boxShadow, width, videoSource, imageSource, textLink, onEnd, playButtonPosition, ...rest
  } = props;

  const minSize = getMinDimensions(size);
  const maxSize = getMaxDimensions(size);

  const videoRef = useRef(null);
  const [showOverlay, toggleOverlay] = useState(true);

  const _handlePlayVideo = () => {
    // Allows user to replay video once reach the end
    if (videoRef?.current?.ended) {
      toggleOverlay(false);
    }
    videoRef?.current?.play();
  };

  const _onVideoEnd = () => {
    toggleOverlay(true);
    if (onEnd) onEnd();
  };

  return (
    <Wrapper width={width} maxSize={maxSize} minSize={minSize}>
      {!videoSource
        ? (
          <NoVideoWrapper width={width} size={size}>
            {size !== 'preview'
              ? (
                <EmptyStateWrapper>
                  <Heading medium color={Colors.Gray}>
                    No Video
                  </Heading>
                  {textLink
                && (
                  <Link
                    variant="text"
                    href={textLink.href}
                    target={textLink.target}
                    onClick={textLink.onClick}
                  >
                    {textLink.children}
                  </Link>
                )}
                </EmptyStateWrapper>
              )
              : (
                <IconWrapper>
                  <Icon mini color={Colors.GrayLight} name="play-circle" />
                </IconWrapper>
              )}
          </NoVideoWrapper>
        )
        : (
          <>
            {size !== 'preview' && (
              <VideoWrapper boxShadow={boxShadow} size={size}>
                <StyledVideoPlayer
                  getRef={videoRef}
                  width="100%"
                  src={videoSource}
                  onEnd={_onVideoEnd}
                  onLoadedData={() => {
                    toggleOverlay(false);
                  }}
                  {...rest}
                />
              </VideoWrapper>

            )}
            <ImageWrapper
              showOverlay={showOverlay}
              boxShadow={boxShadow}
              maxSize={maxSize}
              minSize={minSize}
              size={size}
              onClick={size === 'preview' ? null : _handlePlayVideo}
              width={width}
              noVideoSource={videoSource === ' '}
            >
              <ImageOverlay
                imageSource={imageSource}
                {...(size !== 'preview' && {
                  icon: {
                    name: 'play-circle',
                    position: playButtonPosition || 'bottomRight',
                    padding: getIconPadding(size),
                    small: size !== 'preview',
                    mini: size === 'preview',
                  },
                })}
              />
            </ImageWrapper>
          </>
        )}
    </Wrapper>

  );
};

VideoSquare.defaultProps = {
  width: '100%',
  boxShadow: true,
};

export default VideoSquare;
